import { colorsPalettes, mixins, setFontVariant } from "@similarweb/styles";
import type { IColumnsPickerLiteProps } from "@similarweb/ui-components";
import { ColumnsPickerLite } from "@similarweb/ui-components";
import { ListItemWebsite } from "@similarweb/ui-components";
import categoryService, { ALL } from "app/common/services/categoryService";
import { SearchInput } from "@similarweb/ui-components";
import { MultiCategoriesChipDown } from "pro-features/components/MultiCategoriesChipDown/src/MultiCategoriesChipDown";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { i18nFilter } from "app/filters/i18nFilter";
import { i18nCategoryFilter } from "app/filters/ngFilters";
import { DownloadExcelContainer, SearchContainer } from "app/pages/workspace/StyledComponent";
import { allTrackers } from "app/services/track/track";
import styled from "styled-components";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { chosenSites } from "app/common/services/chosenSites";
import { formatDate } from "@similarweb/pro-logic-components";
import { CompareTabsContainer } from "app/components/compareTabs/CompareTabsContainer";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { useCurrentStateAndParams } from "@uirouter/react";
import { getCategoriesOptions } from "app/common/keyword-research/keyword-search-ads/categoryOptionsUtils";

const TopStyled = styled.div`
    display: flex;
    align-items: center;
    padding: 12px;
`;
const StyledLabel = styled.div`
    ${mixins.setFont({ $color: colorsPalettes.carbon[500], $weight: 300, $size: 16 })};
`;
const ChipWrap = styled.div`
    padding-left: 8px;
    &:first-child {
        padding-left: 0;
    }
`;

const Right = styled.div`
    flex-grow: 0;
    display: flex;
    align-items: center;
    margin-left: 10px;
`;
const ChipdownItem = styled.div`
    flex-grow: 0;
    margin-right: 8px;
    margin-left: 8px;
`;

const FiltersWrapper = styled(FlexRow)`
    background: ${colorsPalettes.bluegrey[100]};
    padding-bottom: 24px;
    flex-wrap: wrap;
    gap: 8px;
`;
const TableHeader = styled.div`
    ${setFontVariant("h5_2", colorsPalettes.carbon[500])};
    padding: 20px 24px;
`;

const SearchContainerWrapper = styled(SearchContainer)`
    border-top: 0;
    border-top: 1px solid ${colorsPalettes.carbon[50]};
`;

const ProductAdsTableTop = (props) => {
    const i18n = i18nFilter();
    const i18nCategory = i18nCategoryFilter();
    const categories = categoryService.getCategories().filter((category) => category.id !== ALL);
    const {
        downloadExcelPermitted,
        excelLink,
        tableColumns,
        onClickToggleColumns,
        onCategorySelected,
        selectedCategory,
        onSearch,
        searchValue,
        showCategorySelector = true,
        showDomainSelector = false,
        onSelectDomain,
        selectedDomain,
    } = props;
    const { params } = useCurrentStateAndParams();
    const isCompare = params.key?.split(",").length > 1;
    const dateFilter = useSubtitleFilters("date");

    const FormattedDate = formatDate(dateFilter[0]?.value?.from, dateFilter[0]?.value?.to);

    const onChange = (value: any) => {
        onSearch(value);
    };

    const onColumnToggle = (key) => {
        onClickToggleColumns(parseInt(key, 10));
    };
    const getColumnsPickerLiteProps = (): Omit<IColumnsPickerLiteProps, "translations"> => {
        const columns = tableColumns.reduce((res, col, index) => {
            if (!col.fixed) {
                return [
                    ...res,
                    {
                        key: index.toString(),
                        displayName: col.displayName,
                        visible: col.visible,
                    },
                ];
            }
            return res;
        }, []);
        return {
            columns,
            onColumnToggle,
            onPickerToggle: () => null,
            maxHeight: 264,
            width: "auto",
        };
    };
    const trackExcelDownload = () => {
        allTrackers.trackEvent("Download", "submit-ok", "Table/Excel");
    };
    const onSelectSite = (selectedDomain: string, numberOfResults: string) => {
        allTrackers.dispatchTrackEvent(
            "Tab",
            "click",
            `Filter Domain/${selectedDomain || ""}/${numberOfResults || ""}`,
        );
        proNavigator.go(proNavigator.current().name, Object.assign(proNavigator.getParams(), { selectedDomain }));
    };
    const domains = chosenSites.sitelistForLegend();
    const selectedDomainObj = selectedDomain ? domains.find((domain) => domain.name === selectedDomain) : domains[0];
    const getDomainsOptions = () => {
        return domains.map(({ name, icon }, index) => {
            return (
                <ListItemWebsite
                    key={index}
                    text={name}
                    img={icon}
                />
            );
        });
    };
    const categorySelector = (
        <ChipWrap>
            <MultiCategoriesChipDown
                categories={getCategoriesOptions(categories)}
                initialSelectedCategories={selectedCategory}
                onDone={onCategorySelected}
                buttonText="All Categories"
                searchPlaceholder="Search for categories"
            />
        </ChipWrap>
    );

    return (
        <>
            {showCategorySelector ? <FiltersWrapper>{categorySelector}</FiltersWrapper> : null}
            <TableHeader>
                {i18n("pla.and.ads.table.header.title")} {FormattedDate}
            </TableHeader>

            {isCompare ? (
                <CompareTabsContainer
                    onSelectSite={onSelectSite}
                    keyName="PlaAndAdsFirstLoad"
                    domainQueryParamName={"selectedDomain"}
                    hideResults={true}
                />
            ) : null}

            <SearchContainerWrapper>
                <SearchInput
                    disableClear={true}
                    defaultValue={searchValue}
                    debounce={400}
                    onChange={onChange}
                    placeholder={i18n("forms.search.placeholder")}
                />
                <Right>
                    <FlexRow>
                        {excelLink() ? (
                            <DownloadExcelContainer>
                                <DownloadButtonMenu
                                    Excel={true}
                                    downloadUrl={excelLink()}
                                    exportFunction={trackExcelDownload}
                                    excelLocked={!downloadExcelPermitted}
                                />
                            </DownloadExcelContainer>
                        ) : null}
                        <div>
                            <ColumnsPickerLite
                                {...getColumnsPickerLiteProps()}
                                withTooltip
                                translations={{
                                    tooltip: i18n("workspace.sales.column.picker"),
                                    getSelectedAmountText: ({ selectedValue, totalValue }) =>
                                        i18n("common.columnsPicker.selectedAmount", {
                                            selected: selectedValue,
                                            total: totalValue,
                                        }),
                                    mandatoryField: i18n("common.columnsPicker.mandatoryField"),
                                    apply: i18n("common.apply"),
                                }}
                            />
                        </div>
                    </FlexRow>
                </Right>
            </SearchContainerWrapper>
        </>
    );
};

export default ProductAdsTableTop;
